import React, { useCallback, useMemo } from 'react';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import styles from './beaconMessageList.module.scss';
import i18n from 'i18next';
import { RecentGroupDropdown } from 'components/RecentGroupDropdown/RecentGroupDropdown';
import { SessionStorageItemKeys } from 'helper/StorageHelper';
import { StickableTanstackTable } from 'components/common/Table/StickableTanstackTable';
import { Page } from 'components/Page/Page';
import { BeaconMessageListDTO } from 'core/beaconMessage/BeaconMessage';
import { Link } from 'react-router-dom';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import {
  faChartArea,
  faEye,
  faPencilAlt
} from '@fortawesome/free-solid-svg-icons';
import { useBeaconMessageListModel } from './BeaconMessageListModel';

export const BeaconMessageList: React.FC = () => {
  const {
    loading,
    isBeaconAdminActor,
    isBeaconExecutiveActor,
    agencyId,
    agencies,
    beaconConnections,
    beaconConnectionId,
    beaconMessagesWithPagination,
    pagination,
    viewModelData,
    columnSettings,
    sortDescriptor,
    selectedBeaconMessages,
    batchOperations,
    setAgencyId,
    setBeaconConnectionId,
    handleTableChange,
    handleOnSearch,
    onHandleSelect,
    onHandleSelectPage,
    clearSelect,
    getReportPath,
    BEACON_MESSAGE_COLUMNS,
    SIZE_PER_PAGE
  } = useBeaconMessageListModel();

  const renderInTopArea = useCallback(() => {
    if (isBeaconAdminActor) {
      return (
        <div className={styles.filterOptions}>
          <div className={styles.title}>
            {i18n.t('beaconMessageList.labels.restaurantOptionsTitle')}
          </div>
          <RecentGroupDropdown
            value={agencyId}
            options={agencies}
            storageKey={SessionStorageItemKeys.AGENCY}
            callback={setAgencyId}
          />
        </div>
      );
    }
    if (isBeaconExecutiveActor) {
      return (
        <div className={styles.filterOptions}>
          <div className={styles.title}>
            {i18n.t('beaconMessageList.labels.beaconConnectionOptionsTitle')}
          </div>
          <RecentGroupDropdown
            value={beaconConnectionId}
            options={beaconConnections}
            storageKey={SessionStorageItemKeys.BEACON_CONNECTION}
            callback={setBeaconConnectionId}
          />
        </div>
      );
    }
  }, [
    agencies,
    agencyId,
    beaconConnections,
    beaconConnectionId,
    isBeaconAdminActor,
    isBeaconExecutiveActor,
    setAgencyId,
    setBeaconConnectionId
  ]);

  const renderListOperations = useCallback(() => {
    return [
      <Link
        key='new-btn'
        to={'/beacon-messages/new'}
        className='btn btn-tertiary btn-sm'
      >
        {i18n.t('beaconMessageList.labels.newBeaconMessage')}
      </Link>
    ];
  }, []);

  const renderRowBtns = useCallback(
    (beaconMessage: BeaconMessageListDTO) => {
      const reportPath = getReportPath(beaconMessage);
      const btns = [
        <Link
          key={`beaconMessageReport-${beaconMessage.l2ObjectId}`}
          to={reportPath}
        >
          <IconWithTooltip
            icon={faChartArea}
            tooltipProps={{
              id: `beaconMessageListReportTip-${beaconMessage.l2ObjectId}`,
              tooltip: i18n.t('beaconMessageList.labels.reportHint')
            }}
          />
        </Link>,
        <Link
          key={`beaconMessageSummary-${beaconMessage.l2ObjectId}`}
          to={`/beacon-messages/${beaconMessage.l2ObjectId}/summary`}
        >
          <IconWithTooltip
            icon={faEye}
            tooltipProps={{
              id: `beaconMessageListSummaryTip-${beaconMessage.l2ObjectId}`,
              tooltip: i18n.t('beaconMessageList.labels.summaryHint')
            }}
          />
        </Link>
      ];
      if (isBeaconExecutiveActor) {
        btns.unshift(
          <Link
            key={`beaconMessageEdit-${beaconMessage.l2ObjectId}`}
            to={`/beacon-messages/${beaconMessage.l2ObjectId}/edit`}
          >
            <IconWithTooltip
              icon={faPencilAlt}
              tooltipProps={{
                id: `beaconMessageListEditTip-${beaconMessage.l2ObjectId}`,
                tooltip: i18n.t('beaconMessageList.labels.editHint')
              }}
            />
          </Link>
        );
      }
      return btns;
    },
    [isBeaconExecutiveActor, getReportPath]
  );

  const actorRelatedTableProps = useMemo(
    () =>
      isBeaconExecutiveActor
        ? {
            selectedRows: selectedBeaconMessages,
            selectFieldName: BEACON_MESSAGE_COLUMNS.NAME,
            onSelect: onHandleSelect,
            onSelectPage: onHandleSelectPage,
            clearSelectedRows: clearSelect
          }
        : {},
    [
      isBeaconExecutiveActor,
      BEACON_MESSAGE_COLUMNS.NAME,
      selectedBeaconMessages,
      onHandleSelect,
      onHandleSelectPage,
      clearSelect
    ]
  );

  if (!beaconMessagesWithPagination || !pagination) {
    return <LoadingIndicator />;
  }

  return (
    <div style={{ height: '100%' }}>
      {loading && <LoadingIndicator />}
      <Page
        title={i18n.t('beaconMessageList.title')}
        renderInTopArea={renderInTopArea}
      >
        <StickableTanstackTable
          stickFirstColumn
          stickLastColumn
          customPadding={false}
          remote
          keyField={BEACON_MESSAGE_COLUMNS.NAME}
          data={viewModelData}
          columns={columnSettings}
          pagination={{
            page: pagination.page,
            sizePerPage: SIZE_PER_PAGE,
            totalSize: pagination.totalCount
          }}
          defaultSorted={sortDescriptor}
          onTableChange={handleTableChange}
          onHandleSearch={handleOnSearch}
          renderListOperations={
            isBeaconExecutiveActor ? renderListOperations : undefined
          }
          renderRowBtns={renderRowBtns}
          batchOperaionConfig={batchOperations}
          noDataIndication={i18n.t('beaconMessageList.labels.nodata')}
          {...actorRelatedTableProps}
          searchbarPlaceholder={i18n.t(
            'beaconMessageList.placeholers.searchBar'
          )}
        />
      </Page>
    </div>
  );
};
